import PropTypes from "prop-types"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import Select from "react-select-plus"

import { useTranslate } from "context/translateContext"
import { capitalizeFirstLetter } from "shared/helpers/strings"
import amazonIcon from "shared/images/accounts/amazon_icon.svg"
import attentionIcon from "shared/images/accounts/attention.svg"
import brokenIcon from "shared/images/accounts/broken.svg"
import ebayIcon from "shared/images/accounts/ebay_icon.svg"
import searchIcon from "shared/images/accounts/search.svg"

import styles from "./AccountDropdown.module.css"

export const AccountDropdown = ({ activeAccount, accountsList, upsell }) => {
  const { t: translate } = useTranslate()
  const [value, setValue] = useState({
    label: getOptionLabel(activeAccount, translate),
    value: activeAccount.value,
  })

  const [tempValue, setTempValue] = useState(value)

  const navigate = useNavigate()

  const setChosenAccount = account => {
    setValue({
      label: getOptionLabel(account, translate),
      value: account.value,
    })
    navigate(account.url)
  }

  const clearValue = () => {
    setTempValue(value)
    setValue()
  }

  const getUpsellContainerClass = () => {
    if (upsell.amazon.status && upsell.ebay.status) return "doubleUpsell"
    if (upsell.amazon.status || upsell.ebay.status) return "singleUpsell"
    return "noUpsell"
  }

  const upsellClass = getUpsellContainerClass()
  const containerClass = `optionContainer${capitalizeFirstLetter(upsellClass)}`

  const menuRenderer = props => {
    const { options, selectValue } = props

    const upsellAccount = (account, url) => {
      selectValue(account)
      setValue({ label: getOptionLabel(account, translate), value: account })
      navigate(url)
    }

    return (
      <div className={styles.accountsContainer}>
        <div className={styles[containerClass]}>
          {options.map((option, index) => (
            <div
              className={
                option?.value === tempValue?.value
                  ? styles.customOptionSelected
                  : styles.customOption
              }
              role="button"
              tabIndex={index}
              key={option?.value}
              onClick={() => selectValue(option)}
            >
              {getOptionLabel(option, translate)}
            </div>
          ))}
        </div>
        <div className={styles[upsellClass]}>
          {upsell.amazon.status && (
            <div
              className={styles.upsell}
              role="button"
              tabIndex={0}
              onClick={() =>
                upsellAccount(
                  {
                    id: upsell.amazon.userId,
                    platform: "amazon",
                    label: "Upsell account",
                    status: "ACTIVE",
                  },
                  `/amazon/${upsell.amazon.userId}/dashboard`
                )
              }
            >
              Add an Amazon account
            </div>
          )}
          {upsell.ebay.status && (
            <div
              className={styles.upsell}
              role="button"
              tabIndex={0}
              onClick={() =>
                upsellAccount(
                  {
                    id: upsell.ebay.userId,
                    platform: "ebay",
                    label: "Upsell account",
                    status: "ACTIVE",
                  },
                  `/ebay/${upsell.ebay.userId}/dashboard`
                )
              }
            >
              Add an eBay account
            </div>
          )}
        </div>
      </div>
    )
  }
  return (
    <Select
      value={value}
      onChange={val => setChosenAccount(val)}
      className={[
        styles.customSelect,
        styles[`${upsellClass}Select`],
        accountsList.length < 5 ? styles.optionShort : "",
      ].join(" ")}
      optionClassName={styles.customOption}
      options={accountsList}
      clearable={false}
      placeholder={translate("search for an account")}
      noResultsText={translate("no accounts")}
      menuRenderer={menuRenderer}
      onOpen={() => clearValue()}
      onClose={() => setValue(tempValue)}
      arrowRenderer={({ isOpen }) => arrowRenderer(isOpen)}
    />
  )
}

AccountDropdown.propTypes = {
  activeAccount: PropTypes.object.isRequired,
  accountsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  upsell: PropTypes.object.isRequired,
}

function arrowRenderer(isOpen) {
  if (isOpen)
    return (
      <span>
        <img src={searchIcon} className={styles.searchIcon} alt="search icon" />
      </span>
    )
  return <span className="icon-arrow-down" />
}

function getStatusIcon(status) {
  if (status === "BROKEN" || status === "NOT_CONNECTED")
    return (
      <img
        src={brokenIcon}
        className={styles.disclaimerIcon}
        alt="broken account icon"
      />
    )
  if (status === "REQUIRES_ATTENTION")
    return (
      <img
        src={attentionIcon}
        className={styles.disclaimerIcon}
        alt="attention account icon"
      />
    )
  return null
}

function getOptionLabel(option, translate) {
  return (
    <div
      className={[
        styles.optionBox,
        option?.status === "BROKEN" || option?.status === "NOT_CONNECTED"
          ? styles.grayedOption
          : "",
      ].join(" ")}
    >
      <div>
        {option?.platform === "ebay" && (
          <img src={ebayIcon} className={styles.ebayIcon} alt="eBay icon" />
        )}
        {option?.platform === "amazon" && (
          <img
            src={amazonIcon}
            className={styles.amazonIcon}
            alt="amazon icon"
          />
        )}
      </div>
      <div className={styles.userName}>{translate(option.label)}</div>{" "}
      <div>{getStatusIcon(option?.status)}</div>
    </div>
  )
}

export default AccountDropdown
